import 'C:\\Projects\\genkei\\zumeco\\static\\folder\\folderState.ts.esbuild-plugin-windicss.css';
import { mdiFormatListCheckbox, mdiImage } from '@mdi/js';
import { createSharedSelector, createSharedState } from 'preact-shared-state-hook';
import type { Doodle, Folder, QueryWhere } from '../../server/src/models';
import { URL_FOLDER } from '../../server/src/urls';
import { loadTenantUsers } from '../shared/data/tenantUsers';
import { sortDoodles, useDoodleOrder } from '../shared/doodleOrder';
import { i18n } from '../shared/i18n';
import { notifyError } from '../shared/notifyError';
import { requireLogin } from '../shared/requireLogin';
import { uploadFile } from '../shared/uploadFile';
import { defaultResponseHandler, isNonNull, withDefaultErrorHandler } from '../shared/utils';
import { getCurrentUser, loadCurrentUser, useCurrentUser } from './data/account';
import { groupDoodles, useDoodleGrouping } from './data/doodleGrouping';
import { deleteDoodle, findDoodleInMemory, loadFolderDoodles, searchDoodles, useDoodleIdDoodleMap, useSearchResultMap } from './data/doodles';
import { loadFolders, loadGuestFolder, useFolders } from './data/folders';
export const ENABLE_HANDOVER = false;
type DoodlesQuery = Readonly<{
  kind: 'folder';
  folderId: string;
} | {
  kind: 'search';
  where: QueryWhere;
}>;
const parseQuery = (): DoodlesQuery => {
  const folder = {
    kind: 'folder',
    folderId: location.pathname.match(/^\/folders\/(.*)$/)?.[1] || ''
  } as const;
  const whereJson = new URLSearchParams(location.search).get('where');
  if (!whereJson) {
    return folder;
  }
  let where: QueryWhere;
  try {
    where = JSON.parse(whereJson) as QueryWhere;
  } catch (error) {
    console.warn('ill-formed where query', error);
    return folder;
  }
  if (!Array.isArray(where?.filename?.containsAll)) {
    console.warn('invalid where query');
    return folder;
  }
  return {
    kind: 'search',
    where
  } as const;
};
addEventListener('popstate', () => void setCurrentDoodlesQuery(parseQuery()));
addEventListener('click', event => {
  if (event.button === 0 && !event.ctrlKey && !event.shiftKey && !event.metaKey && event.target instanceof HTMLAnchorElement) {
    const match = event.target.getAttribute('href')?.match(/^\/folders\/(.*)$/);
    if (match) {
      event.preventDefault();
      void setCurrentDoodlesQuery({
        kind: 'folder',
        folderId: match[1]
      });
    }
  }
});
const [useCurrentDoodlesQuery, _setCurrentDoodlesQuery] = createSharedState<DoodlesQuery>(parseQuery());
export const getCurrentDoodlesQuery = useCurrentDoodlesQuery.snapshot;
export const setCurrentDoodlesQuery = (query: DoodlesQuery, noPushState = false) => {
  {
    const pathname = URL_FOLDER(query.kind === 'search' ? undefined : query.folderId);
    const search = query.kind === 'search' ? `?where=${encodeURIComponent(JSON.stringify(query.where))}` : '';
    if (!noPushState && (location.pathname !== pathname || location.search !== search)) {
      history.pushState(0, '', `${pathname}${search}`);
    }
  }
  _setCurrentDoodlesQuery(query);
  if (query.kind === 'search') {
    return searchDoodles(query.where);
  } else {
    return loadFolderDoodles(query.folderId);
  }
};
export const useCurrentFolder = createSharedSelector([useCurrentUser, useCurrentDoodlesQuery, useFolders], (currentUser, query, folders): Folder | undefined => {
  if (!currentUser || query.kind !== 'folder') {
    return;
  }
  if (!query.folderId) {
    return {
      id: '',
      name: i18n.rootFolderName,
      ownerUserId: currentUser.uid,
      users: {
        [currentUser.uid]: currentUser
      }
    };
  }
  return folders.find(folder => folder.id === query.folderId);
});
export const useCurrentDoodles = createSharedSelector([useCurrentDoodlesQuery, useDoodleIdDoodleMap, useFolders, useSearchResultMap, useDoodleOrder, useDoodleGrouping, useCurrentUser] as const, (query, doodleIdDoodleMap, folders, searchResultMap, order, grouping, currentUser): {
  message?: string;
  grouped?: never;
  doodles: readonly Doodle[];
} | {
  message?: string;
  grouped: true;
  groupedDoodles: Record<string, readonly Doodle[]>;
} => {
  if (query.kind === 'search') {
    const result = searchResultMap.get(JSON.stringify(query.where));
    const doodles = result?.doodleIds.map(findDoodleInMemory).filter(isNonNull);
    const message = result?.hitsTooMany ? i18n.searchResult.hitsTooMany : undefined;
    return {
      doodles: doodles ? sortDoodles(doodles, order).slice(0, 100) : [],
      message
    };
  } else {
    const doodles = [...doodleIdDoodleMap.values()].filter(doodle => doodle.parentFolderId === query.folderId);
    if (grouping && folders.find(folder => folder.id === query.folderId)?.tenantSharing) {
      return {
        grouped: true,
        groupedDoodles: groupDoodles(doodles, grouping, order, currentUser?.uid)
      };
    } else {
      return {
        doodles: doodles ? sortDoodles(doodles, order) : []
      };
    }
  }
});
export const [useSelectedDoodles, setSelectedDoodles] = createSharedState<readonly Doodle[] | null>(null);
export const toggleSelectDoodle = (doodle: Doodle) => setSelectedDoodles(doodles => doodles && (doodles.includes(doodle) ? doodles.filter(d => d !== doodle) : [...doodles, doodle]));
createSharedSelector([useCurrentDoodlesQuery], () => setSelectedDoodles(null));
export const deleteSelectedDoodles = async () => {
  const deletingDoodles = useSelectedDoodles.snapshot();
  if (!deletingDoodles?.length) {
    return;
  }
  return await Promise.all(deletingDoodles.map(doodle => deleteDoodle(doodle)));
};
export const uploadFiles = async (files: ArrayLike<File>) => {
  const query = getCurrentDoodlesQuery();
  if (query.kind !== 'folder') {
    throw Error();
  }
  const parentFolderId = query.folderId;
  const overlay = document.getElementsByClassName('overlay')[0];
  overlay.textContent = i18n.uploading;
  overlay.classList.add('shown');
  try {
    if (files.length === 1) {
      const url = await uploadFile(files[0], parentFolderId);
      url && (location.href = url);
    } else {
      for (let i = 0; i < files.length; i++) {
        overlay.textContent = `${i18n.uploading} ${i + 1}/${files.length}\n\n${files[i].name}`;
        await uploadFile(files[i], parentFolderId);
      }
      await Promise.all([loadFolderDoodles(parentFolderId, true), withDefaultErrorHandler(loadCurrentUser(), notifyError)]);
    }
  } finally {
    overlay.classList.remove('shown');
  }
};
export const doodleListFormats = [['card', i18n.doodleListFormats.card, mdiImage], ['table', i18n.doodleListFormats.table, mdiFormatListCheckbox]] as const;
export type DoodleListFormat = typeof doodleListFormats[number][0];
const STORAGE_KEY_DOODLE_LIST_FORMAT = 'zumeco:folders:doodleListFormat';
const initialDoodleListFormat = localStorage.getItem(STORAGE_KEY_DOODLE_LIST_FORMAT);
const [useDoodleListFormat, _setDoodleListFormat] = createSharedState<DoodleListFormat>(doodleListFormats.some(([format]) => format === initialDoodleListFormat) ? initialDoodleListFormat as DoodleListFormat : doodleListFormats[0][0]);
export const setDoodleListFormat = (format: DoodleListFormat) => {
  localStorage.setItem(STORAGE_KEY_DOODLE_LIST_FORMAT, format);
  _setDoodleListFormat(format);
};
export { useCurrentDoodlesQuery, useDoodleListFormat };
export const init = async (rootElement: HTMLElement) => {
  try {
    const query = parseQuery();
    const folderId = query.kind === 'folder' ? query.folderId : undefined;
    const currentUserResponse = await loadCurrentUser(folderId);
    if (!currentUserResponse.ok) {
      if (folderId && [401, 402, 403].includes(currentUserResponse.status)) {
        topbar.hide();
        await requireLogin(rootElement, getCurrentUser());
        location.reload();
        return;
      }
      return defaultResponseHandler(currentUserResponse, notifyError);
    }
    const currentUser = getCurrentUser();
    if (!currentUser) {
      return notifyError(i18n.errorOccurred);
    }
    if (currentUser.guest) {
      const [folderResponse] = await Promise.all([loadGuestFolder(folderId!), setCurrentDoodlesQuery(query, true)]);
      return folderResponse.ok || defaultResponseHandler(folderResponse, notifyError);
    }
    const [foldersResponse] = await Promise.all([loadFolders(), setCurrentDoodlesQuery(query, true)]);
    if (!foldersResponse.ok) {
      return defaultResponseHandler(foldersResponse, notifyError);
    }
    void loadTenantUsers(currentUser, currentUser.primary_tenant_id);
  } catch {
    notifyError(i18n.networkErrorOccurred);
  } finally {
    topbar.hide();
  }
};